import { useCallback, useEffect, useRef } from 'react';
import { useGameStore } from '../store/gameStore';
import type { ClientMessage, ServerMessage } from '../../../shared/types';

/** Same-origin WS URL: https://host/ludo → wss://host/ludo/ws.
 *  In production Caddy strips the base path, so the server sees /ws. */
function resolveWsUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const base = import.meta.env.BASE_URL || '/';
  const path = base.replace(/\/$/, '') + '/ws';
  return `${proto}://${window.location.host}${path}`;
}

const RETRY_BASE_MS = 800;
const RETRY_MAX_MS = 8000;

export interface GameSocketApi {
  createRoom: (playerName: string) => void;
  joinRoom: (roomCode: string, playerName: string) => void;
  setReady: (ready: boolean) => void;
  startGame: () => void;
  setTimeLimit: (seconds: number) => void;
  rollDice: () => void;
  moveToken: (tokenIndex: number) => void;
  sendChat: (text: string) => void;
  leave: () => void;
  addBot: (difficulty: 'easy' | 'medium' | 'hard') => void;
  removeBot: (playerId: string) => void;
  setBotDifficulty: (playerId: string, difficulty: 'easy' | 'medium' | 'hard') => void;
  socket: WebSocket | null;
}

export function useGameSocket(): GameSocketApi {
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(1);
  const closedByUser = useRef(false);
  const store = useGameStore;

  const connect = useCallback(() => {
    if (closedByUser.current) return;
    const ws = new WebSocket(resolveWsUrl());
    wsRef.current = ws;

    ws.onopen = () => {
      retryRef.current = 1;
      store.getState().setConnected(true);
      store.getState().setError(null);
      const s = store.getState();
      if (s.roomCode && s.myPlayerId && s.identity) {
        ws.send(JSON.stringify({ type: 'RECONNECT', roomCode: s.roomCode, playerId: s.myPlayerId, identity: s.identity }));
      }
    };

    ws.onmessage = (event) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(event.data as string);
      } catch {
        return;
      }
      handleServerMessage(msg);
    };

    ws.onclose = () => {
      store.getState().setConnected(false);
      if (closedByUser.current) return;
      const delay = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * retryRef.current);
      retryRef.current *= 2;
      window.setTimeout(connect, delay);
    };

    ws.onerror = () => ws.close();
  }, [store]);

  useEffect(() => {
    closedByUser.current = false;
    connect();
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && (!wsRef.current || wsRef.current.readyState > 1)) {
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      closedByUser.current = true;
      wsRef.current?.close();
    };
  }, [connect]);

  const send = useCallback((msg: ClientMessage) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  const api: GameSocketApi = {
    socket: wsRef.current,
    createRoom: (playerName) => send({ type: 'CREATE_ROOM', playerName }),
    joinRoom: (roomCode, playerName) => send({ type: 'JOIN_ROOM', roomCode, playerName }),
    setReady: (ready) => send({ type: 'SET_READY', ready }),
    startGame: () => send({ type: 'START_GAME' }),
    setTimeLimit: (seconds) => send({ type: 'SET_TIME_LIMIT', seconds }),
    rollDice: () => send({ type: 'ROLL_DICE' }),
    moveToken: (tokenIndex) => send({ type: 'MOVE_TOKEN', tokenIndex }),
    sendChat: (text) => send({ type: 'SEND_CHAT', text }),
    addBot: (difficulty) => send({ type: 'ADD_BOT', difficulty }),
    removeBot: (playerId) => send({ type: 'REMOVE_BOT', playerId }),
    setBotDifficulty: (playerId, difficulty) => send({ type: 'SET_BOT_DIFFICULTY', playerId, difficulty }),
    leave: () => {
      closedByUser.current = false;
      const s = store.getState();
      s.reset();
      if (wsRef.current) wsRef.current.close();
      window.setTimeout(() => {
        closedByUser.current = false;
        connect();
      }, 400);
    },
  };
  api.socket = wsRef.current;

  return api;
}

function handleServerMessage(msg: ServerMessage): void {
  const s = useGameStore.getState();
  switch (msg.type) {
    case 'ERROR':
      s.setError(msg.message);
      window.setTimeout(() => s.setError(null), 4000);
      break;
    case 'ROOM_CREATED':
    case 'ROOM_JOINED':
      s.setConnectionInfo({ roomCode: msg.roomCode, playerId: msg.playerId, identity: msg.identity });
      break;
    case 'RECONNECTED':
      s.setError(null);
      break;
    case 'ROOM_SNAPSHOT':
      s.setSnapshot(msg.snapshot);
      break;
    case 'PRIVATE_STATE':
      s.setPrivateState(msg.state);
      break;
    case 'GAME_STARTED':
    case 'GAME_ENDED':
      s.setSnapshot(msg.snapshot);
      if ('state' in msg && msg.state) s.setPrivateState(msg.state);
      break;
    case 'CHAT':
      s.pushChat(msg.entry);
      break;
    case 'PLAYER_JOINED':
    case 'PLAYER_LEFT':
      // Full snapshot follows via ROOM_SNAPSHOT.
      break;
  }
}